import { useContext, useEffect, useRef, useState } from 'react';
import { ReactComponent as SwordIcon } from '@assets/svg/sword.svg';
import Avatar from '@components/avatar';
import useReduxState from '@hooks/useReduxState';
import useSound from 'use-sound';
import jackpotSound from '@assets/audio/rolling-jackpot-and-win.mp3';
import styles from './winner.module.scss';
import { IGameUserObject } from '@api/types/generalGame';
import { playersColorContext } from '@views/jackpot';
import { useSpring, animated, easings } from 'react-spring';
import useJackpotRoom from '@hooks/useJackpotRoom';
import cn from 'classnames';

const PlayerList = ({
    list,
    // winnerTicket,
    // bank,
    winnerId,
}: {
    list: { player: IGameUserObject; playerIndex: number }[];
    winnerTicket: number;
    bank: number;
    winnerId: string;
}) => {
    const playerList: any[] = [];

    for (let i = 0; i < 28; i++) {
        playerList.push(...list);
    }

    const { players: playersColors } = useContext(playersColorContext);

    const [, setContainerWidth] = useState<number>(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const pixelBefore = 64 * 8 * 16;

    let lastArrayIndex = 0;

    while (
        lastArrayIndex < list.length &&
        !(list[lastArrayIndex].player.id === winnerId)
    ) {
        lastArrayIndex++;
    }
    const stopPixel = Math.floor(64 * (lastArrayIndex - 1)) + 32;

    useEffect(() => {
        if (containerRef.current) {
            setContainerWidth(containerRef.current.clientWidth);
        }
    }, []);

    const style = useSpring({
        config: {
            duration: 9700,
            easing: easings.easeOutCubic,
        },
        from: {
            x: '0px',
        },
        to: {
            x: `-${pixelBefore + stopPixel}px`,
        },
    });

    return (
        <animated.div ref={containerRef} className={styles.list} style={style}>
            {playerList.map(({ player, playerIndex }, idx) => {
                const borderColor = playersColors[playerIndex].color;
                return (
                    <div key={player.username + idx}>
                        <Avatar
                            src={player.avatar_url}
                            alt={player.username}
                            width="64"
                            borderColor={borderColor.slice(
                                1,
                                borderColor.length,
                            )}
                            className={styles.player}
                        />
                    </div>
                );
            })}
        </animated.div>
    );
};

const WinnerSelector = (): JSX.Element | null => {
    const [playJackpotSound] = useSound(jackpotSound);
    const [{ rooms }] = useReduxState((state) => state.jackpot);
    const { currentRoom } = useJackpotRoom();

    // eslint-disable-next-line no-unused-vars
    const [closeStats, setCloseStats] = useState(false);
    const game = rooms[currentRoom].current_game;

    useEffect(() => {
        let timer: any;
        if (game.game_metadata.status === 'finished') {
            timer = setTimeout(() => {
                setCloseStats(true);
                setTimeout(() => {
                    setCloseStats(false);
                }, 10000);
            }, 15000);
        }
        return () => clearTimeout(timer);
    }, [game.game_metadata.status]);

    const playersPercentage = game.stakes.map((stake) => {
        const playerIndex = game.players.findIndex(
            (player) => player.user.id == stake.userId,
        );
        const player = game.players.find(
            (player) => player.user.id == stake.userId,
        );
        return {
            items: Math.round((stake.stake / game.bank) * 8),
            player: player!,
            playerIndex,
        };
    });

    const playersArray: { player: IGameUserObject; playerIndex: number }[] = [];

    playersPercentage.forEach((player) => {
        Array.from(Array(player.items).keys()).forEach(() => {
            playersArray.push({
                player: player.player.user,
                playerIndex: player.playerIndex,
            });
        });
    });

    useEffect(() => {
        playJackpotSound();
    }, []);

    /* Components */

    return (
        <>
            {game.game_metadata.status === 'finished' && (
                <div
                    className={cn({
                        [styles.opening_trans]: !closeStats,
                        [styles.closing_trans]: closeStats,
                    })}
                >
                    <section className={styles.container}>
                        <div className={styles.container_list}>
                            <PlayerList
                                winnerId={game.winner_id}
                                list={playersArray}
                                winnerTicket={game.roll_stake}
                                bank={game.bank}
                            />
                        </div>
                        <SwordIcon className={styles.swordicon} />
                    </section>
                </div>
            )}
        </>
    );
};
export default WinnerSelector;
